import type { CSSProperties, ReactNode } from 'react'

/**
 * Topbar — the app chrome's horizontal half.
 *
 * COPIED FROM MONZA (`packages/ui/src/components/TopbarSys.tsx`) per the copy-not-share rule,
 * and matches David's spec directly: the topbar hosts nav Back, the current page title, and
 * Save / Cancel (docs/portals/design-guidelines.md §1). Renamed `TopbarSys` -> `Topbar` (monza's
 * "Sys" suffix marks its framework-chrome subsystem, which we have no counterpart to).
 *
 * PURE / presentational: it renders the state it is HANDED and calls the callbacks. It owns no
 * routing — DashboardShell feeds it.
 *
 * Anatomy: `[hamburger] [brand] [back?] [title] ········· [actions]`
 *   - **Back** shows only when `canGoBack` — never at depth 0.
 *
 * ★★ NO SAVE / CANCEL, and that is the difference from admin's copy of this file. This portal is
 * read-only as a security posture, not as a feature list: every route it calls is a GET, so a
 * commit button here could never do anything. Admin's version keeps the pair MOUNTED-but-hidden
 * so it can slide in on the dirty edge; carrying that over would leave two inert buttons in the
 * DOM of a portal that can never be dirty — dead weight, and (as the live drive showed) they
 * still read out in the page's text.
 *
 * STRIPPED on the way in: monza's theme-engine couplings (`fontSlotStyle` and the
 * `RecordValidationError` type both reach into `@monza/client`) are replaced by our static
 * tokens and a local type — the whole point of the copy rule is that nothing here depends on
 * monza at runtime.
 */

export interface TopbarProps {
  /** Current page title; the topbar's own slot, not the page's. */
  title?: string
  /** The nav stack can pop — shows Back. False at depth 0 -> no Back. */
  canGoBack?: boolean
  /** Toggle the side menu (the hamburger). */
  onMenuToggle?: () => void
  /** The topbar Back. */
  onBack?: () => void
  /** Leftmost brand slot. */
  brand?: ReactNode
  /** Right-aligned chrome (user, sign-out) — host-supplied. */
  actions?: ReactNode
}

const bar: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  height: 56,
  padding: '0 16px',
  borderBottom: '1px solid var(--border)',
  // ★ The CHROME surface — the same token as the side menu, never the page background. The
  // topbar and the side menu are ONE surface that happens to wrap the page in an L, and theming
  // them apart is never what you want. (In monza this bar was hardcoded to --background and so
  // silently tracked the PAGE colour; --topbar-bg exists to stop exactly that.)
  background: 'var(--topbar-bg, var(--sidebar))',
  color: 'var(--sidebar-foreground, var(--foreground))',
  flex: '0 0 auto',
}

const cluster: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }

const titleStyle: CSSProperties = {
  fontWeight: 600,
  fontSize: 'var(--fs-page-title)',
  fontFamily: 'var(--ff-page-title)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}

// A chrome icon button (menu toggle / back). No `background`: it carries `.admin-option` so it
// hovers with the same tint as every other clickable option (index.css). An inline background —
// even `transparent` — would outrank that rule.
const iconBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  borderRadius: 'var(--radius)',
  border: 'none',
  color: 'inherit',
  cursor: 'pointer',
  flex: '0 0 auto',
}

/** Inline SVGs, so the chrome needs no icon dependency. */
function BackIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m15 18-6-6 6-6" />
    </svg>
  )
}

function MenuIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M3 6h18M3 12h18M3 18h18" />
    </svg>
  )
}

export function Topbar({ title, canGoBack = false, onMenuToggle, onBack, brand, actions }: TopbarProps) {
  return (
    <header style={bar}>
      <div style={cluster}>
        {onMenuToggle && (
          <button type="button" className="admin-option" style={iconBtn} onClick={onMenuToggle} aria-label="Toggle menu">
            <MenuIcon />
          </button>
        )}
        {brand}
        {canGoBack && (
          <button type="button" className="admin-option" style={iconBtn} onClick={onBack} aria-label="Back">
            <BackIcon />
          </button>
        )}
        {/* ⚠ HIDDEN ON PHONE. The bar is [hamburger][brand][title]···[user][sign-out] and at 390px
            the brand, the page title and the account chip all fight for the same strip — "Yoreflix
            Organiz… ZDF Studios…" all truncated (David). The page title is the most redundant of
            the three: the active menu item already names the screen, and the menu is one tap away.
            Class-driven so it cannot be beaten by the inline style, per this app's layout rule. */}
        {title != null && <span className="dash-topbar-title" style={titleStyle}>{title}</span>}
      </div>
      <div style={cluster}>{actions}</div>
    </header>
  )
}
